import { createContext, useContext } from "react";
import {
  DISPLAY_CURRENCY_OPTIONS,
  SOURCE_CURRENCY,
  formatPrice,
} from "../utils/currency";

/**
 * The currency context, split out from CurrencyProvider on purpose (#64).
 *
 * Every price surface consumes `useCurrency`. If that hook lived in the
 * provider module, importing it would also pull in the account API — and
 * therefore axios — so a unit test for a card tile would need an axios mock to
 * render at all. Consumers depend on this file; only the app root depends on
 * the provider.
 */

/**
 * Used when a component renders without the provider (isolated tests, mostly).
 * It formats through the same boundary in the source currency — the honest
 * fallback — rather than throwing, so a price can never fail to render over
 * missing context. The app itself always mounts the provider.
 */
export const FALLBACK = {
  preference: "auto",
  displayCurrency: SOURCE_CURRENCY,
  rates: null,
  options: DISPLAY_CURRENCY_OPTIONS,
  setPreference: () => {},
  // Same two-argument shape as the provider's: the second argument is the
  // currency the AMOUNT is in, not the one the reader picked.
  format: (amount, amountCurrency) => formatPrice(amount, { amountCurrency }),
  formatAmount: (amount, amountCurrency) =>
    formatPrice(amount, { amountCurrency }).text,
};

export const CurrencyContext = createContext(FALLBACK);

export const useCurrency = () => useContext(CurrencyContext);
